import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { PayeeCasesService } from '../payee-cases.service';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import axios, { AxiosError } from 'axios';
import { DebtorOption } from '../types';

@Component({
  selector: 'payee-case-debtor-name-filter',
  templateUrl: './case-debtor-name-filter.component.html',
  styleUrls: ['./case-debtor-name-filter.component.scss']
})
export class CaseDebtorNameFilterComponent implements OnInit, OnDestroy {
  private readonly destroy = new Subject<void>();
  readonly debtorNameControl = new FormControl('', [
    Validators.required,
    Validators.minLength(3)
  ]);

  debtorOptions: DebtorOption[] = [];
  loading = false;

  dropdownOpen = false;

  get selected() {
    return this.payeeCasesService.debtor_name.value;
  }

  constructor(
    private payeeCasesService: PayeeCasesService,
  ) { }

  ngOnInit(): void {
    this.payeeCasesService.debtor_name
      .pipe(takeUntil(this.destroy))
      .subscribe(v => this.debtorNameControl.patchValue(v));

    // Subscribe to input changes for autocomplete
    this.debtorNameControl.valueChanges
      .pipe(
        takeUntil(this.destroy),
        debounceTime(1000),
        distinctUntilChanged(),
      )
      .subscribe(async name => {
        await this.setDebtors(name);
      });
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  onPaste(event: ClipboardEvent): void {
    this.debtorNameControl.markAsTouched();
    setTimeout(() => {
      this.applyFilter();
    });
  }

  private async setDebtors(name?: string): Promise<void> {
    if (!this.payeeCasesService.getDebtorsPath.value) {
      console.warn('Get debtors path is not set!');
      return;
    }

    if (!this.dropdownOpen || !name || name.length < 3) {
      return;
    }

    try {
      this.loading = true;

      const response = await axios.get<{ debtors: DebtorOption[]; }>(
        `${environment.baseUrl}/${this.payeeCasesService.getDebtorsPath.value}`,
        {
          params: {
            name
          }
        }
      );

      this.debtorOptions = response.data.debtors;
    } catch (error: AxiosError | unknown) {
      if (error instanceof AxiosError) {
        console.error('Network error while searching debtors:', error.response?.data);
      } else {
        console.error('Unknown error searching debtors:', error);
      }
    } finally {
      this.loading = false;
    }
  }

  applyFilter(): void {
    if (this.debtorNameControl.invalid) {
      this.debtorNameControl.markAsDirty();
      return;
    }

    this.payeeCasesService.debtor_name.next(this.debtorNameControl.value);
    this.dropdownOpen = false;
  }

  clearFilter(): void {
    this.payeeCasesService.debtor_name.next(null);
    this.dropdownOpen = false;
  }

  displayWith(selected: string): string {
    return selected;
  }
}